import { useCallback, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useSubmitLock } from '@/hooks/useSubmitLock'

type PlaylistCreateDialogProps = {
  open: boolean
  onClose: () => void
  onCreate: (name: string) => Promise<void>
}

/**
 * プレイリスト新規作成ダイアログ
 */
export const PlaylistCreateDialog = ({ open, onClose, onCreate }: PlaylistCreateDialogProps) => {
  const [name, setName] = useState('')
  const { isSubmitting: isCreating, run } = useSubmitLock()

  const handleCreate = useCallback(async () => {
    const trimmed = name.trim()
    if (!trimmed) return

    await run(async () => {
      await onCreate(trimmed)
      setName('')
      onClose()
    })
  }, [name, onCreate, onClose, run])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleCreate()
      }
    },
    [handleCreate],
  )

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setName('')
        onClose()
      }
    },
    [onClose],
  )

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>新しいプレイリスト</DialogTitle>
          <DialogDescription>プレイリスト名を入力してください</DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="playlist-name">名前</Label>
          <Input
            id="playlist-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="例: ドライブ用"
            autoFocus
          />
        </div>

        <DialogFooter>
          <div className="flex gap-2 w-full">
            <Button variant="outline" onClick={onClose} className="flex-1">
              キャンセル
            </Button>
            <Button onClick={handleCreate} disabled={!name.trim() || isCreating} className="flex-1">
              作成
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
